import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader, DataTable, Chip, EmptyState, type Column } from '../ds/index'
import { FilterTiles } from '../components/Viz'
import { useApi } from '../lib/useApi'
import { ago, money } from '../lib/format'
import type { Lead } from '../lib/types'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { LeadFilterBar, useLeadFilters, useFilterOptions } from '../components/LeadFilters'
import { URGENCY } from './MyLeads'
import { ScoreChip } from '../components/ScoreChip'
import { SortSelect, useSortParam, type LeadSort } from '../components/SortSelect'

type View = 'leads' | 'open' | 'sold' | 'unrouted' | 'uncategorized' | 'not_leads' | 'held' | 'location_unknown' | 'unassigned'
type Buyer = { id: string; name: string; billed_cents: number | null; billed_at: string | null; notified_at: string | null; outcome: string | null }
type Row = Lead & { buyers: Buyer[]; category_id: string | null; suggested_category?: string | null; person_name?: string | null }

const SORTS: LeadSort[] = ['newest', 'oldest', 'az', 'za']

const STAGE: Record<string, { label: string; tone?: 'pos' | 'warn' | 'neg' | 'accent' }> = {
  queued: { label: 'Waiting to screen', tone: 'accent' },
  no_reps: { label: 'Held: no reps in group', tone: 'warn' },
  too_old: { label: 'Too old: came before a rep' },
  not_lead: { label: 'Not a lead' },
  uncategorized: { label: 'Uncategorized' },
  lead: { label: 'Being routed', tone: 'accent' },
  routed: { label: 'Routed' },
  failed: { label: 'Failed', tone: 'neg' },
}

function assignmentSummary(l: Row) {
  if (l.stage !== 'routed') return <Chip tone={STAGE[l.stage]?.tone} dot>{STAGE[l.stage]?.label || l.stage}</Chip>
  if (!l.assignments.length) return <Chip tone="warn" dot>Nobody to send it to</Chip>
  const open = l.assignments.filter((a) => a.status === 'open')
  const replied = l.assignments.filter((a) => a.status === 'responded').length
  if (replied) return <Chip tone="pos" dot>Replied{l.assignments.length > 1 ? ` ${replied}/${l.assignments.length}` : ''}</Chip>
  if (open.length) return <Chip tone="accent" dot>Waiting on {open.map((a) => a.rep_name.split(' ')[0]).join(', ')}</Chip>
  return <Chip dot>Skipped</Chip>
}

/** Who the lead went to: sold (with the price), or still waiting on the rep's reply. */
function buyersCell(l: Row) {
  if (!l.buyers.length) return <span className="muted">none</span>
  return (
    <span className="stack-xs">
      {l.buyers.map((b) => (
        <span key={b.id} className="nowrap">
          {b.billed_at ? <strong>{b.name}</strong> : <span className="muted">{b.name}</span>}
          {b.billed_at ? <span className="muted"> · {b.billed_cents != null ? money(b.billed_cents) : 'no price'}</span> : <span className="muted"> · not yet</span>}
        </span>
      ))}
    </span>
  )
}

const soldCents = (l: Row) => l.buyers.reduce((t, b) => t + (b.billed_at && b.billed_cents ? b.billed_cents : 0), 0)

export default function Leads() {
  // The view and the filters live in the address, so the lead page's back link
  // returns to exactly this list and a filtered board can be sent to someone.
  const [params, setParams] = useSearchParams()
  const views: View[] = ['leads', 'open', 'sold', 'unrouted', 'uncategorized', 'not_leads', 'held', 'location_unknown', 'unassigned']
  const view = (views.includes(params.get('view') as View) ? params.get('view') : 'leads') as View
  const f = useLeadFilters('all')
  const options = useFilterOptions()
  const setView = (v: View) => { const n = new URLSearchParams(params); n.set('view', v); setParams(n, { replace: true }) }
  const nav = useNavigate()
  const [sort, setSort] = useSortParam(SORTS, 'newest')
  const { data, error, loading } = useApi<{ leads: Row[]; counts: Record<View, number>; truncated: boolean; everything_to_reps?: boolean; zone_routing?: boolean }>(`/api/leads?view=${view}&sort=${sort}&${f.query}`)

  const cols: Column<Row>[] = [
    { key: 'when', header: 'Seen', cell: (r) => <span className="nowrap muted">{ago(r.first_seen_at)}</span>, sortBy: (r) => r.first_seen_at },
    { key: 'what', header: 'Request', cell: (r) => <><strong>{r.summary || r.body_text?.slice(0, 90) || '(text purged)'}</strong><div className="muted nowrap">{[r.person_name || r.author_display, r.group_name].filter(Boolean).join(' · ')}</div></>, sortBy: (r) => r.summary || r.body_text || '' },
    { key: 'cat', header: 'Category', cell: (r) => r.category_name ? <span className="nowrap">{r.category_name}</span> : r.suggested_category ? <span className="muted nowrap" title="Uncategorized: what they asked for">{r.suggested_category}?</span> : <span className="muted">none</span>, sortBy: (r) => r.category_name || r.suggested_category || '~' },
    { key: 'area', header: 'Area', cell: (r) => r.area_name ? <span className="nowrap">{r.area_name}</span> : <span className="muted">unclear</span>, sortBy: (r) => r.area_name || '~' },
    { key: 'score', header: 'Score', numeric: true, cell: (r) => r.lead_score != null ? <ScoreChip score={r.lead_score} /> : <span className="muted">none</span>, sortBy: (r) => r.lead_score ?? -1 },
    { key: 'urg', header: 'Timing', cell: (r) => r.urgency && URGENCY[r.urgency]?.tone ? <Chip tone={URGENCY[r.urgency].tone}>{URGENCY[r.urgency].label}</Chip> : <span className="muted nowrap">{r.urgency ? URGENCY[r.urgency]?.label : ''}</span>, sortBy: (r) => ['today', 'this_week', 'flexible', 'unknown'].indexOf(r.urgency || 'unknown') },
    { key: 'state', header: 'Rep', cell: assignmentSummary, sortBy: (r) => r.assignments.map((a) => a.rep_name).join(', ') || '~' },
    { key: 'buyers', header: 'Sold to', cell: buyersCell, sortBy: soldCents },
  ]

  return (
    <>
      <PageHeader title="Lead board" subtitle="Every post the screening called a request, who it went to, who bought it, and whether the rep replied."
                  actions={<Link className="btn btn-ghost" to={`/categories${f.carry()}`}>By category</Link>} />
      <LeadFilterBar f={f} options={options} />
      <FilterTiles<View> value={view} onChange={setView} tiles={[
        { value: 'leads', label: 'All leads', count: data?.counts.leads, hint: 'screened as requests' },
        { value: 'open', label: 'Waiting on a rep', count: data?.counts.open, tone: 'warn', hint: 'sent, not replied' },
        { value: 'sold', label: 'Sold', count: data?.counts.sold, tone: 'pos', hint: 'a rep replied, buyers billed' },
        { value: 'unrouted', label: 'Nobody to send to', count: data?.counts.unrouted, tone: 'warn', hint: 'no matching buyer' },
        { value: 'uncategorized', label: 'Uncategorized', count: data?.counts.uncategorized, hint: data?.everything_to_reps ? 'a service we do not sell yet, still sent to reps' : 'asked for something we do not sell yet' },
        { value: 'not_leads', label: 'Not leads', count: data?.counts.not_leads, hint: data?.everything_to_reps ? 'AI says not a request, still sent to reps' : 'screened out or too old' },
        { value: 'held', label: 'Held or failed', count: data?.counts.held, tone: 'neg', hint: 'waiting or errored' },
        ...(data?.zone_routing ? [
          { value: 'location_unknown' as View, label: 'Location unknown', count: data?.counts.location_unknown, tone: 'warn' as const, hint: 'not sent until someone picks the zone' },
          { value: 'unassigned' as View, label: 'Unassigned', count: data?.counts.unassigned, tone: 'warn' as const, hint: 'no business holds that trade in that zone' },
        ] : []),
      ]} />
      <div className="row-tight" style={{ justifyContent: 'flex-end', marginTop: 'var(--s-4)' }}>
        <SortSelect value={sort} onChange={setSort} choices={SORTS} />
      </div>
      {error && <ErrorBlock error={error} />}
      {loading && !data ? <LoadingBlock /> : (
        <div className="panel panel-flush">
          <DataTable columns={cols} rows={data?.leads || []} rowKey={(r) => r.id} onRowClick={(r) => { const q = new URLSearchParams(params); q.set('view', view); nav(`/leads/${r.id}?${q}`) }}
                     empty={<EmptyState title="Nothing in this view" body={f.active ? 'Nothing matches these filters.' : view === 'open' ? 'Every lead sent to a rep has a reply or a skip.' : undefined} />} />
          {data?.truncated && <p className="muted" style={{ padding: 'var(--s-4) var(--s-6)' }}>{sort === 'newest' ? 'Showing the newest 500. Narrow the dates or filters to see older ones.' : sort === 'oldest' ? 'Showing the oldest 500. Narrow the dates or filters to see newer ones.' : 'Showing the first 500 in this order. Narrow the dates or filters to see the rest.'}</p>}
        </div>
      )}
    </>
  )
}
